import { format, formatDistanceToNow, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Currency
export function formatCurrency(value: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value ?? 0);
}

export function formatCurrencyAmount(value: number | null | undefined): string {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value ?? 0);
}

export function parseCurrency(value: string): number {
  return parseMoney(value) ?? 0;
}

export function formatCurrencyInput(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  const number = parseInt(digits, 10) / 100;
  return formatCurrency(number);
}

export function formatMoneyInput(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export function formatMoneyDigits(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  return formatMoneyInput(Number.parseInt(digits, 10) / 100);
}

export function parseMoney(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const normalized = trimmed
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

// Dates
export function formatDate(date: string | Date, pattern = 'dd/MM/yyyy'): string {
  try {
    const d = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(d)) return '-';
    return format(d, pattern, { locale: ptBR });
  } catch {
    return '-';
  }
}

/**
 * Formata uma DATA PURA (coluna `@db.Date` da API). Esses campos chegam como ISO
 * com sufixo Z (ex.: "2026-06-29T00:00:00.000Z"); formatá-los com `formatDate`
 * direto desloca um dia em fusos negativos (BRT -3 -> 28/06). Aqui usamos só o
 * trecho YYYY-MM-DD, que `parseISO` lê como meia-noite LOCAL - imune a fuso.
 * Use sempre que exibir folga/bloqueio/qualquer coluna `@db.Date`.
 */
export function formatDateOnly(date: string | Date, pattern = 'dd/MM/yyyy'): string {
  const value = typeof date === 'string' ? date.slice(0, 10) : date;
  return formatDate(value, pattern);
}

export function formatDateTime(date: string | Date): string {
  return formatDate(date, "dd/MM/yyyy 'às' HH:mm");
}

export function formatDateLong(date: string | Date): string {
  return formatDate(date, "EEEE, d 'de' MMMM 'de' yyyy");
}

export function formatDateShort(date: string | Date): string {
  return formatDate(date, 'dd MMM yyyy');
}

export function formatRelative(date: string | Date): string {
  try {
    const d = typeof date === 'string' ? parseISO(date) : date;
    if (!isValid(d)) return '-';
    return formatDistanceToNow(d, { addSuffix: true, locale: ptBR });
  } catch {
    return '-';
  }
}

/**
 * Formato curto e defensivo para celulas/cards: aceita null/undefined/ISO
 * invalido (retorna '-'). Usado em listas/admin onde a coluna pode estar
 * vazia (ex: ultimo acesso, ultimo teste, etc).
 *
 * Variantes:
 *  - formatDateTimeBR: data + hora (toLocaleString pt-BR)
 *  - formatDateBR: so data (toLocaleDateString pt-BR)
 */
export function formatDateTimeBR(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('pt-BR');
}

export function formatDateBR(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('pt-BR');
}

/**
 * Tempo relativo "humanizado curto" - retorna 'agora', 'ha Ns', 'ha N min',
 * 'ha N h' nas primeiras 24h, depois cai pra data formatada pt-BR.
 *
 * Diferente do formatRelative (date-fns formatDistanceToNow) que retorna
 * frases mais longas ("ha cerca de 2 horas"). Usar este em listas/cards
 * onde o espaco e estreito (status pages, monitoring, secrets list).
 */
export function formatRelativeShort(
  iso: string | null | undefined,
  opts: { withTime?: boolean } = {},
): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  const diffMs = Date.now() - d.getTime();
  // Tolera ate 1s atras como "agora" (evita "ha 0s" feio quando o
  // momento e quase exato).
  if (diffMs < 1000) return 'agora';
  const seconds = Math.round(diffMs / 1000);
  if (seconds < 60) return `há ${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  return opts.withTime
    ? d.toLocaleString('pt-BR')
    : d.toLocaleDateString('pt-BR');
}

/**
 * Formata valor em centavos para BRL pt-BR. Aceita null/undefined (retorna
 * "R$ 0,00"). Usado em admin/plans, admin/tenants e similares onde os
 * valores monetarios vem do banco como int (centavos).
 */
export function formatCentsBR(cents: number | null | undefined): string {
  return formatCurrency((cents ?? 0) / 100);
}

/**
 * Formata data ISO (YYYY-MM-DD ou completa) para DD/MM/YYYY usando
 * string-slice. Diferente de formatDate (date-fns que pode shiftar
 * por TZ), este preserva exatamente a data salva. Usar para campos
 * "data-only" como aniversario.
 *
 * null/undefined/invalido -> '-'.
 */
export function formatBirthDate(iso: string | null | undefined): string {
  if (!iso) return '-';
  const ymd = iso.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '-';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Converte ISO ou Date pra string YYYY-MM-DD aceita por <input type="date">.
 * Vazio quando entrada e null/undefined/invalido - HTML <input> entende
 * string vazia como "sem valor".
 *
 * Para STRING ISO, preferimos slice(0, 10) em vez de Date.toISOString()
 * porque toISOString converte para UTC e pode mudar o dia quando o
 * ISO original tem timezone diferente (ex: "2026-06-15T22:00:00-03:00"
 * vira UTC "2026-06-16T01:00:00Z" - dia mudou). Para campos data-only
 * como aniversario, o slice direto preserva a data salva no banco.
 */
export function toDateInputValue(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  if (typeof iso === 'string') {
    if (iso.length < 10) return '';
    const ymd = iso.slice(0, 10);
    // Sanity check minimo: YYYY-MM-DD
    if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '';
    return ymd;
  }
  if (Number.isNaN(iso.getTime())) return '';
  return iso.toISOString().slice(0, 10);
}

// Time
export function formatTime(date: string | Date): string {
  return formatDate(date, 'HH:mm');
}

export function formatDuration(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (m === 0) return `${h}h`;
  return `${h}h${m}min`;
}

// Phone
export function formatPhone(value: string | null | undefined): string {
  if (!value) return '';
  let digits = value.replace(/\D/g, '');
  if (digits.length === 0) return '';

  // Strip Brazilian country code (55) on stored 12/13-digit numbers so we
  // format the local DDD + number, not the country code.
  if ((digits.length === 12 || digits.length === 13) && digits.startsWith('55')) {
    digits = digits.slice(2);
  }

  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
}

export function unformatPhone(value: string): string {
  return value.replace(/\D/g, '');
}

// CEP - mascara 00000-000 (8 digitos)
export function formatCep(value: string | null | undefined): string {
  const digits = (value ?? '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

// CPF / CNPJ
export function formatCpfCnpj(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 11) {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) {
      return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    }
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  }
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

export function unformatCpfCnpj(value: string): string {
  return value.replace(/\D/g, '');
}

export function isValidCpfOrCnpj(value: string): boolean {
  const digits = unformatCpfCnpj(value);
  return digits.length === 11 || digits.length === 14;
}

// Numbers
export function formatNumber(value: number): string {
  return new Intl.NumberFormat('pt-BR').format(value);
}

export function formatPercent(value: number, decimals = 1): string {
  return `${value.toFixed(decimals)}%`;
}

// Initials
export function getInitials(name: unknown): string {
  if (!name || typeof name !== 'string') return '?';
  return name
    .trim()
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((n) => n[0].toUpperCase())
    .join('') || '?';
}

/**
 * Formata bytes (em MB) escolhendo a melhor unidade. Aceita numeros em MB
 * (formato vindo do backend SystemHealthService).
 * - >= 1024 MB -> GB com 2 casas decimais (ate 99.99 GB) ou 1 (>= 100)
 * - 1 MB ate 1023 MB -> MB inteiro
 * - 0 MB -> "0 MB"
 *
 * Exemplos:
 *   formatMb(19502)  => "19,04 GB"
 *   formatMb(7216)   => "7,05 GB"
 *   formatMb(395)    => "395 MB"
 *   formatMb(0)      => "0 MB"
 */
export function formatMb(mb: number | null | undefined): string {
  const v = typeof mb === "number" && Number.isFinite(mb) ? mb : 0;
  if (v < 1) return "0 MB";
  if (v < 1024) return `${Math.round(v).toLocaleString("pt-BR")} MB`;
  const gb = v / 1024;
  const decimals = gb >= 100 ? 1 : 2;
  return `${gb.toLocaleString("pt-BR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })} GB`;
}

/**
 * Normaliza o campo `specialties` do barbeiro (CSV no backend, array no front)
 * para um array de strings limpo. Aceita array (passa direto) ou string CSV.
 */
export function toSpecialtiesArray(val: unknown): string[] {
  if (Array.isArray(val)) return val as string[];
  if (typeof val === "string" && val.trim()) {
    return val.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
}
